import { styled } from 'dripsy';
import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';

import {
  BaseText,
  InviteFriendSvg,
  Separator,
  Button,
  SettingsLayout,
} from '@components';
import { screens, ScreensNameType } from '@navigation';
import { useLink, useNavOptionsSet } from '@hooks';
import { ShareService, ToastService } from '@services';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { IS_WEB } from '@utils';

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.InviteFriend,
];

const Container = styled(View)({
  maxWidth: [null, 420],
  flex: 1,
  py: '$6',
  alignItems: 'center',
  alignSelf: [null, 'center'],
});

const PictureContainer = styled(View)({
  width: [343, '100%'],
  height: [343, 419],
  aspectRatio: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const Heading = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xlarge',
  fontWeight: 'semi',
  textAlign: 'center',
});

const Description = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
  textAlign: 'center',
});

const ButtonContainer = styled(View)({
  width: ['100%', 165],
});

export const InviteFriendScreen = observer(() => {
  const { t } = useTranslation();
  const { buildFullLink } = useLink();
  const viewer = useViewerModel();

  useNavOptionsSet({ title: t('inviteFriend.title') });

  async function shareInviteLink() {
    try {
      const invite = viewer.currentUser?.invite.settings;

      const inviteLink = buildFullLink({
        screen: screens.SwitchNavigator,
        params: {
          screen: screens.AuthNavigator,
          params: {
            screen: screens.Referral,
            params: { invite },
          },
        },
      });

      const link = await viewer.createShortLink.run({
        link: inviteLink,
      });

      if (!link) {
        throw new Error(t('errors.sharedLink'));
      }

      await ShareService.shareUrl(
        { url: link.shortLink },
        { clipboardMessage: t('inviteFriend.linkCopied') },
      );
    } catch (error) {
      const message = (error as Error).message;
      ToastService.showError(message ?? t('errors.default'));
    }
  }

  return (
    <SettingsLayout
      breadcrumbRoutes={BREADCRUMB_ROUTES}
      title={t('inviteFriend.title')}
    >
      <Container>
        <PictureContainer>
          <InviteFriendSvg />
        </PictureContainer>

        <Heading>{`${t('inviteFriend.heading')} 🥳`}</Heading>

        <Separator size="$4" />
        <Description>{t('inviteFriend.description')}</Description>

        <Separator size={['$10', '$7']} />
        <ButtonContainer>
          <Button
            onPress={shareInviteLink}
            isLoading={viewer.createShortLink.inProgress}
          >
            {t('inviteFriend.button', {
              context: IS_WEB ? 'copyLink' : 'share',
            })}
          </Button>
        </ButtonContainer>
      </Container>
    </SettingsLayout>
  );
});
